import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { useApolloClient, useLazyQuery, useMutation, useQuery } from "@apollo/client"
import "react-awesome-lightbox/build/style.css"
import "../Material/ListingHeader.css"
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore"
import { firestoreDb } from "../Components/firebase"
import ReactMarkdown from 'react-markdown'
import moment from 'moment-timezone'
import withApolloClient from '../withApolloClient'
import { CurrentUserContext } from '../Context/CurrentUserContext'
import {
  ASSIGN_CONVERSATION,
  SUBSCRIBE_CONVERSATION,
  UNSUBSCRIBE_CONVERSATION,
  UPDATE_CONVERSATION,
  UPDATE_MESSAGE_TOPIC,
} from './Mutations'
import RepAvatar from '../Material/RepAvatar'
import { GET_CONVERSATION, GET_INBOX_MEMBERS, GET_ROUND_ROBIN_GROUPS, ASSIGNED_UNREAD_COUNT_QUERY, MESSAGE_ATTACHMENTS, GET_TOPICS } from './Queries'
import ExternalIntegrations from '../Drivers/Driver/Detail/DetailsNew/Components/ExternalIntegrations'
import { debounce } from 'lodash'
import { Link } from 'react-router-dom'
import Loader from '../Material/Loader'
import MessageComposer from './SendMessage/MessageComposer'
import CommsMessageCard from './CommsMessageCard'
import { RepsQuery, UserQuery } from '../Functions/querys'
import CommsEditConversationModal from './CommsEditConversationModal'
import CommsSnoozeModal from './CommsSnoozeModal'
import { RepTypeAhead, isGroupOption } from '../TaskManagement/TaskComponents/TypeAheadComponents'

const COMMS_MESSAGES_COLLECTION_NAME = "messaging_message"
const NOTES_COLLECTION_NAME = "messaging_conversationnote"
const ASSIGNEE_DEFAULT_RESULTS = 20
const SUBSCRIBER_DEFAULT_RESULTS = 20
const LAST_MESSAGE_SCROLL_GAP = 8

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif', 'avif'])
const NOTE_MENTION_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/g
const RAW_URL_REGEX = /(^|[\s(])((https?:\/\/[^\s<]+))/g

const extractFilename = (urlOrName) => {
  if (!urlOrName) return 'Attachment'
  const source = urlOrName.split('?')[0]
  const parts = source.split('/')
  const lastPart = parts[parts.length - 1] || urlOrName
  try {
    return decodeURIComponent(lastPart)
  } catch (error) {
    return lastPart
  }
}

const extensionFromName = (name) => {
  if (!name) return ''
  const parts = name.split('.')
  if (parts.length < 2) return ''
  return parts.pop().toLowerCase()
}

const isImageAttachment = ({ mimeType, extension, name, url }) => {
  if (mimeType && mimeType.startsWith('image/')) return true
  const ext = extension || extensionFromName(name || extractFilename(url))
  return IMAGE_EXTENSIONS.has(ext)
}

const formatUserDisplayName = (user) => {
  if (!user) return ''
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return fullName || user.username || 'Unassigned'
}

const dedupeMembersByUser = (members = []) => {
  const seen = new Set()
  return members.filter((member) => {
    const userId = member?.user?.id
    if (!userId || seen.has(userId)) return false
    seen.add(userId)
    return true
  })
}

const dedupeUsersById = (users = []) => {
  const seen = new Set()
  return users.filter((user) => {
    if (!user?.id || seen.has(user.id)) return false
    seen.add(user.id)
    return true
  })
}

const getConversationInboxIds = (conversation) => {
  const inboxIds = []
  const seen = new Set()

  const addInboxId = (id) => {
    if (!id || seen.has(id)) return
    seen.add(id)
    inboxIds.push(id)
  }

  (conversation?.inboxes?.edges || []).forEach((edge) => addInboxId(edge?.node?.id))

  return inboxIds
}

const normalizeConversationNoteMarkdown = (note) => {
  if (!note) return ''

  return note
    .replace(NOTE_MENTION_REGEX, '[@$1](mention:$2)')
    .replace(RAW_URL_REGEX, (match, prefix, url) => `${prefix}[${url}](${url})`)
}

const CommsConversation = ({ 
  conversationIdFromParent, 
  createResponseForPrompt, 
  slimView = false, 
  onConversationArchived = null,
  ...props }) => {
  const apolloClient = useApolloClient()
  const { currentUser } = useContext(CurrentUserContext)
  const [messages, setMessages] = useState([])
  const [notes, setNotes] = useState([])
  const [conversationId, setConversationId] = useState(conversationIdFromParent)
  const [actionMenuOpen, setActionMenuOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [snoozeModalOpen, setSnoozeModalOpen] = useState(false)
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('')
  const [selectedAssigneeUser, setSelectedAssigneeUser] = useState(null)
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('')
  const [assigneeSearchUsers, setAssigneeSearchUsers] = useState([])
  const [assigneeOptionsLoading, setAssigneeOptionsLoading] = useState(false)
  const [assignError, setAssignError] = useState(null)
  const [selectedSubscriberId, setSelectedSubscriberId] = useState('')
  const [selectedSubscriberUser, setSelectedSubscriberUser] = useState(null)
  const [subscriberSearchTerm, setSubscriberSearchTerm] = useState('')
  const [subscriberSearchReps, setSubscriberSearchReps] = useState([])
  const [subscriberOptionsLoading, setSubscriberOptionsLoading] = useState(false)
  const [subscriptionError, setSubscriptionError] = useState(null)
  const [subscribersMenuOpen, setSubscribersMenuOpen] = useState(false)
  const messagesContainerRef = useRef(null)
  const lastMessageRef = useRef(null)
  const subscribersMenuRef = useRef(null)
  const subscribersButtonRef = useRef(null)
  const actionMenuRef = useRef(null)
  const actionButtonRef = useRef(null)

  const prevTotalMessagesCountRef = useRef(0)

  const [updateConversationMutation, {loading: updateConversationLoading}] = useMutation(UPDATE_CONVERSATION, {
    refetchQueries: [{ query: GET_CONVERSATION, variables: { id: conversationId } }, { query: ASSIGNED_UNREAD_COUNT_QUERY }],
    onError: (error) => console.error('Update conversation error', error),
  })

  const [editingTopicMessageId, setEditingTopicMessageId] = useState(null)
  const [updateMessageTopic] = useMutation(UPDATE_MESSAGE_TOPIC)
  const { data: topicsData } = useQuery(GET_TOPICS, { fetchPolicy: 'cache-first' })
  const topics = topicsData?.allTopics?.edges?.map(e => e.node) ?? []

  const handleTopicChange = (messageGrapheneId, topicId) => {
    updateMessageTopic({ variables: { messageId: messageGrapheneId, topicId: topicId || null } })
    setEditingTopicMessageId(null)
  }

  const {
    data: conversationData,
    loading: conversationLoading,
    error: conversationError,
    refetch: refetchConversation,
  } = useQuery(GET_CONVERSATION, {
    variables: { id: conversationId },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
  })

  const conversation = conversationData?.conversation || null
  const conversationInboxIds = useMemo(
    () => getConversationInboxIds(conversation),
    [conversation]
  )

  const { data: inboxMembersData } = useQuery(GET_INBOX_MEMBERS, {
    variables: { inboxIds: conversationInboxIds, first: 100 },
    skip: conversationInboxIds.length === 0,
    fetchPolicy: 'cache-first',
  })

  const { data: roundRobinGroupsData } = useQuery(GET_ROUND_ROBIN_GROUPS, {
    variables: { inboxIds: conversationInboxIds },
    skip: conversationInboxIds.length === 0,
    fetchPolicy: 'cache-first',
  })

  const roundRobinGroups = useMemo(
    () => (roundRobinGroupsData?.roundRobinGroups || []).filter(Boolean),
    [roundRobinGroupsData]
  )

  const conversationMembers = useMemo(() => {
    if (!inboxMembersData?.inboxAccessUsers) return []
    return dedupeMembersByUser(
      inboxMembersData.inboxAccessUsers.filter(node => node?.user)
    )
  }, [inboxMembersData])

  const assignableMembers = useMemo(
    () => conversationMembers,
    [conversationMembers]
  )

  const isCurrentUserAssigned = useMemo(() => (
    conversation?.assignedTo &&
    currentUser &&
    conversation?.assignedTo?.username === currentUser?.username
  ), [conversation?.assignedTo, currentUser])

  const currentUserIdString = useMemo(() => (
    currentUser?.id ? String(currentUser.id) : null
  ), [currentUser?.id])

  const assigneeRequestRef = useRef(0)
  const subscriberRequestRef = useRef(0)

  const assigneeUsers = useMemo(() => (
    dedupeUsersById([
      selectedAssigneeUser,
      conversation?.assignedTo,
      ...assigneeSearchUsers,
    ].filter(Boolean))
  ), [assigneeSearchUsers, conversation?.assignedTo, selectedAssigneeUser])

  const selectedAssigneeValue = useMemo(
    () => selectedAssigneeUser || selectedAssigneeId || null,
    [selectedAssigneeId, selectedAssigneeUser]
  )

  const debouncedRefetch = useMemo(
    () => debounce(() => refetchConversation(), 250),
    [refetchConversation]
  )

  useEffect(() => {
    return () => { debouncedRefetch.cancel() }
  }, [debouncedRefetch])

  useEffect(() => {
    if (conversationInboxIds.length === 0) {
      setAssigneeSearchUsers([])
      setAssigneeOptionsLoading(false)
      return
    }

    const requestId = assigneeRequestRef.current + 1
    assigneeRequestRef.current = requestId
    const trimmedSearchTerm = assigneeSearchTerm.trim()
    const timer = setTimeout(async () => {
      setAssigneeOptionsLoading(true)
      try {
        const { data } = await apolloClient.query({
          query: GET_INBOX_MEMBERS,
          variables: {
            inboxIds: conversationInboxIds,
            first: ASSIGNEE_DEFAULT_RESULTS,
            searchTerm: trimmedSearchTerm || null,
          },
          fetchPolicy: 'network-only',
        })

        if (assigneeRequestRef.current !== requestId) return

        const users = dedupeUsersById(
          (data?.inboxAccessUsers || [])
            .map((entry) => entry?.user)
            .filter(Boolean)
        )
        setAssigneeSearchUsers(users)
      } catch (error) {
        if (assigneeRequestRef.current !== requestId) return
        console.error('Load assignee options error', error)
        setAssigneeSearchUsers([])
      } finally {
        if (assigneeRequestRef.current === requestId) {
          setAssigneeOptionsLoading(false)
        }
      }
    }, trimmedSearchTerm ? 200 : 0)

    return () => clearTimeout(timer)
  }, [apolloClient, assigneeSearchTerm, conversationInboxIds])

  useEffect(() => {
    if (!subscribersMenuOpen) {
      subscriberRequestRef.current += 1
      setSubscriberOptionsLoading(false)
      return
    }

    const requestId = subscriberRequestRef.current + 1
    subscriberRequestRef.current = requestId
    const trimmedSearchTerm = subscriberSearchTerm.trim()
    const timer = setTimeout(async () => {
      setSubscriberOptionsLoading(true)
      try {
        const { data } = await apolloClient.query({
          query: RepsQuery,
          variables: {
            first: SUBSCRIBER_DEFAULT_RESULTS,
            searchTerm: trimmedSearchTerm || null,
          },
          fetchPolicy: 'cache-first',
        })

        if (subscriberRequestRef.current !== requestId) return

        const reps = dedupeUsersById(
          (data?.allReps?.edges || [])
            .map((edge) => edge?.node)
            .filter(Boolean)
        )
        setSubscriberSearchReps(reps)
      } catch (error) {
        if (subscriberRequestRef.current !== requestId) return
        console.error('Load subscriber options error', error)
        setSubscriberSearchReps([])
      } finally {
        if (subscriberRequestRef.current === requestId) {
          setSubscriberOptionsLoading(false)
        }
      }
    }, trimmedSearchTerm ? 200 : 0)

    return () => clearTimeout(timer)
  }, [apolloClient, subscriberSearchTerm, subscribersMenuOpen])

  const baseConversationId = conversation?.id

  const [assignConversationMutation, { loading: assignLoading }] = useMutation(ASSIGN_CONVERSATION, {
    refetchQueries: [{ query: ASSIGNED_UNREAD_COUNT_QUERY }],
    onCompleted: (data) => {
      const result = data?.messagingAssignConversation
      if (result?.ok) {
        debouncedRefetch()
        setAssignError(null)
      } else if (result?.errors?.length) {
        setAssignError(result.errors.join(', '))
      } else {
        setAssignError('Unable to assign conversation')
      }
    },
    onError: (error) => {
      console.error('Assign conversation error', error)
      setAssignError(error.message || 'Error assigning conversation')
    },
  })

  const [subscribeConversationMutation, { loading: subscribeLoading }] = useMutation(SUBSCRIBE_CONVERSATION, {
    onCompleted: (data) => {
      const result = data?.messagingSubscribeConversation
      if (result?.ok) {
        debouncedRefetch()
        setSubscriptionError(null)
      } else if (result?.errors?.length) {
        setSubscriptionError(result.errors.join(', '))
      } else {
        setSubscriptionError('Unable to subscribe to conversation')
      }
    },
    onError: (error) => {
      console.error('Subscribe conversation error', error)
      setSubscriptionError(error.message || 'Error subscribing to conversation')
    },
  })

  const [unsubscribeConversationMutation, { loading: unsubscribeLoading }] = useMutation(UNSUBSCRIBE_CONVERSATION, {
    onCompleted: (data) => {
      const result = data?.messagingUnsubscribeConversation
      if (result?.ok) {
        debouncedRefetch()
        setSubscriptionError(null)
      } else if (result?.errors?.length) {
        setSubscriptionError(result.errors.join(', '))
      } else {
        setSubscriptionError('Unable to unsubscribe from conversation')
      }
    },
    onError: (error) => {
      console.error('Unsubscribe conversation error', error)
      setSubscriptionError(error.message || 'Error unsubscribing from conversation')
    },
  })

  // ✅ Keep local conversationId in sync with parent
  useEffect(() => {
    setConversationId(conversationIdFromParent)
  }, [conversationIdFromParent])

  // ✅ Reset per-conversation counters when switching conversations
  useEffect(() => {
    prevTotalMessagesCountRef.current = 0
  }, [conversationId])

  useEffect(() => {
    if (!conversationId) {
      setMessages([])
      return
    }

    const q = query(
      collection(firestoreDb, COMMS_MESSAGES_COLLECTION_NAME),
      where("conversation_graphene_id", "==", conversationId),
      orderBy("timestamp", "desc"),
      limit(100),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs
      const nextMessages = docs.map(doc => ({ id: doc.id, ...doc.data(), type: "message" }))
      setMessages(nextMessages)

      // only refetch on growth (new msg)
      if (docs.length > prevTotalMessagesCountRef.current) {
        debouncedRefetch()
      }
      prevTotalMessagesCountRef.current = docs.length
    })

    return () => unsubscribe()
  }, [conversationId])

  useEffect(() => {
    if (!conversationId) {
      setNotes([])
      return
    }

    const q = query(
      collection(firestoreDb, NOTES_COLLECTION_NAME),
      where("conversation_graphene_id", "==", conversationId),
      orderBy("date_added", "desc"),
      limit(50),
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedNotes = []
      snapshot.forEach((doc) => {
        fetchedNotes.push({ id: doc.id, ...doc.data(), type: "note" })
      })
      setNotes(fetchedNotes)
    })

    return () => unsubscribe()
  }, [conversationId])

  const combinedFeed = useMemo(() => {
    return [...messages, ...notes].sort(
      (a, b) => new Date(a.timestamp || a.date_added) - new Date(b.timestamp || b.date_added)
    )
  }, [messages, notes])

  // Answers which feed entry is the newest actual message, skipping the interleaved notes.
  const lastMessageIndex = useMemo(() => {
    for (let i = combinedFeed.length - 1; i >= 0; i--) {
      if (combinedFeed[i].type === "message") return i
    }
    return -1
  }, [combinedFeed])

  const lastMessageId = lastMessageIndex >= 0 ? combinedFeed[lastMessageIndex].id : null

  const [fetchAttachmentsByIds, { data: attachmentsData }] = useLazyQuery(MESSAGE_ATTACHMENTS)

  const baseAttachmentsMap = useMemo(() => {
    const map = new Map()

    combinedFeed.forEach(message => {
      if (!message?.attachments && !message?.attachment_urls) return

      if (message.attachments) {
        message.attachments?.forEach((attachment, index) => {
          const attachmentId = attachment?.graphene_id ?? `${message.graphene_id}-${index}`

          map.set(attachmentId, {
            attachmentId,
            grapheneId: attachment?.graphene_id,
            url: attachment?.url ?? attachment,
            name: attachment?.name ?? `Attachment ${index + 1}`,
            dateAdded: message.date_added,
            isImage: attachment?.extension && isImageAttachment({
              extension: attachment.extension,
              name: attachment?.name,
              url: attachment?.url
            }),
          })
        })
        return
      }

      if (message.attachment_urls) {
        message.attachment_urls?.forEach((url, index) => {
          const attachmentId = `${message.graphene_id}-${index}`
          map.set(attachmentId, {
            attachmentId,
            url,
            name: extractFilename(url),
            dateAdded: message.date_added,
            isImage: isImageAttachment({ name: extractFilename(url), url })
          })
        })
      }
    })

    return map
  }, [combinedFeed])

  const [attachmentsMap, setAttachmentsMap] = useState(baseAttachmentsMap)
  useEffect(() => {
    setAttachmentsMap(baseAttachmentsMap)
  }, [baseAttachmentsMap])

  const SIX_DAYS = 6 * 24 * 60 * 60 * 1000
  const expiredAttachmentIds = useMemo(() => {
    const ids = []

    attachmentsMap.forEach((attachment) => {
      const { url, grapheneId, dateAdded } = attachment

      const isExpiredGcsUrl =
        typeof url === "string" &&
        grapheneId &&
        url.includes("storage.googleapis.com") &&
        Date.now() - new Date(dateAdded).getTime() > SIX_DAYS

      if (isExpiredGcsUrl) ids.push(grapheneId)
    })

    return ids
  }, [attachmentsMap])

  const fetchedIdsRef = useRef(new Set())
  useEffect(() => {
    const idsToFetch = expiredAttachmentIds.filter(id => !fetchedIdsRef.current.has(id))
    if (idsToFetch.length === 0) return

    idsToFetch.forEach(id => fetchedIdsRef.current.add(id))

    fetchAttachmentsByIds({
      variables: { ids: idsToFetch },
    })
  }, [expiredAttachmentIds, fetchAttachmentsByIds])

  useEffect(() => {
    const nodes = attachmentsData?.allMessageAttachments?.edges
      ?.map(e => e?.node)
      .filter(Boolean)

    if (!nodes?.length) return

    setAttachmentsMap(prev => {
      const next = new Map(prev)

      next.forEach((value, key) => {
        if (!value.grapheneId) return

        const updated = nodes.find(n => n.id === value.grapheneId)
        if (!updated) return

        next.set(key, {
          ...value,
          url: updated.url,
        })
      })

      return next
    })
  }, [attachmentsData])

  // Seeds the reply composer's To/Cc. Unlike the suggestion below this cannot look at messages[0]:
  // once we send a reply that slot holds our own outbound message and the prefill would vanish.
  const lastInboundMessage = useMemo(
    () => messages.find(message => message?.is_inbound) || null,
    [messages]
  )

  const lastInboundSuggestedResponse = useMemo(() => {
    if (messages.length === 0) return ''
    const lastMessage = messages[0]
    if (!lastMessage?.is_inbound) return ''
    return (
      lastMessage?.ai_agent_suggested_response ||
      lastMessage?.message?.ai_agent_suggested_response ||
      ''
    )
  }, [messages])

  // False while the Loader is up, so the scroll container isn't in the DOM yet.
  const hasRenderedFeed = Boolean(conversation)

  // Anchor the last message's top edge, not the container bottom, so long emails open at the start.
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const lastMessage = lastMessageRef.current
    if (!lastMessage) {
      container.scrollTop = container.scrollHeight
      return
    }

    const offset = lastMessage.getBoundingClientRect().top - container.getBoundingClientRect().top
    container.scrollTop += offset - LAST_MESSAGE_SCROLL_GAP
    // Keyed on the newest message, not `conversation`, which changes identity on every refetch.
    // hasRenderedFeed re-runs this once the container exists — Firestore can arrive before it does.
  }, [conversationId, lastMessageId, hasRenderedFeed])

  useEffect(() => {
    if (conversation?.assignedTo?.id) {
      setSelectedAssigneeId(conversation.assignedTo.id)
      setSelectedAssigneeUser({
        id: conversation.assignedTo.id,
        username: conversation.assignedTo.username,
        firstName: conversation.assignedTo.firstName,
        lastName: conversation.assignedTo.lastName,
      })
    } else if (!assignLoading) {
      setSelectedAssigneeId('')
      setSelectedAssigneeUser(null)
    }
  }, [
    assignLoading,
    conversation?.assignedTo?.firstName,
    conversation?.assignedTo?.id,
    conversation?.assignedTo?.lastName,
    conversation?.assignedTo?.username,
  ])

  const handleAssigneeChange = async (option) => {
    const previousAssigneeId = selectedAssigneeId
    const previousAssigneeUser = selectedAssigneeUser
    const isGroup = isGroupOption(option)
    const value = option?.value || ''

    setSelectedAssigneeId(isGroup ? '' : value)
    setSelectedAssigneeUser(isGroup ? null : (option?.rep || option?.user || null))
    setAssigneeSearchTerm('')
    setAssignError(null)

    if (!value || !baseConversationId) return

    try {
      const variables = isGroup
        ? { conversationId: baseConversationId, roundRobinGroupId: value }
        : { conversationId: baseConversationId, userId: value }
      const result = await assignConversationMutation({ variables })
      if (!result?.data?.messagingAssignConversation?.ok) {
        setSelectedAssigneeId(previousAssigneeId)
        setSelectedAssigneeUser(previousAssigneeUser)
      }
    } catch (error) {
      setSelectedAssigneeId(previousAssigneeId)
      setSelectedAssigneeUser(previousAssigneeUser)
    }
  }

  const handleSubscriberChange = (option) => {
    const value = option?.value || ''
    setSelectedSubscriberId(value)
    setSelectedSubscriberUser(option?.rep || option?.user || null)
    setSubscriberSearchTerm('')
    setSubscriptionError(null)
  }

  // Only mark as read when a new message arrives and the conversation is currently unread
  const prevMessagesLengthRef = useRef(messages.length)
  useEffect(() => {
    // Only mark as read if:
    // - There is a conversation
    // - The current user is the assigned agent
    // - The conversation is currently unread
    // - There are new messages (messages.length increases)
    if (!baseConversationId || !currentUserIdString || !isCurrentUserAssigned) return
    if (!conversation?.isReadByAssignedAgent && messages.length > prevMessagesLengthRef.current) {
      updateConversationMutation({
        variables: { conversationId: baseConversationId, isReadByAssignedAgent: true },
        refetchQueries: [UserQuery],
      })
    }
    prevMessagesLengthRef.current = messages.length
  }, [baseConversationId, currentUserIdString, isCurrentUserAssigned, conversation?.isReadByAssignedAgent, messages.length, updateConversationMutation])

  const subscribers = useMemo(() => (
    (conversation?.subscribers?.edges || [])
      .map(edge => edge?.node)
      .filter(Boolean)
  ), [conversation?.subscribers?.edges])
  const subscriberIdSet = useMemo(() => new Set(subscribers.map(subscriber => subscriber.id)), [subscribers])

  const availableSubscriberReps = useMemo(() => (
    dedupeUsersById([
      selectedSubscriberUser,
      ...subscriberSearchReps,
    ].filter(Boolean)).filter(rep => !subscriberIdSet.has(rep.id))
  ), [selectedSubscriberUser, subscriberIdSet, subscriberSearchReps])

  const selectedSubscriberValue = useMemo(
    () => selectedSubscriberUser || selectedSubscriberId || null,
    [selectedSubscriberId, selectedSubscriberUser]
  )

  useEffect(() => {
    if (selectedSubscriberId && subscriberIdSet.has(selectedSubscriberId)) {
      setSelectedSubscriberId('')
      setSelectedSubscriberUser(null)
    }
  }, [selectedSubscriberId, subscriberIdSet])

  useEffect(() => {
    setSelectedSubscriberId('')
    setSelectedSubscriberUser(null)
    setSubscriberSearchTerm('')
    setSubscriberSearchReps([])
    setSubscriberOptionsLoading(false)
    setSubscriptionError(null)
  }, [conversation?.id])

  useEffect(() => {
    setSubscribersMenuOpen(false)
    setActionMenuOpen(false)
    setEditModalOpen(false)
  }, [conversation?.id])

  useEffect(() => {
    if (!subscribersMenuOpen) return

    const handleClickOutside = (event) => {
      if (
        subscribersMenuRef.current &&
        !subscribersMenuRef.current.contains(event.target) &&
        !subscribersButtonRef.current?.contains(event.target)
      ) {
        setSubscribersMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [subscribersMenuOpen])

  useEffect(() => {
    if (!actionMenuOpen) return

    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target) &&
        !actionButtonRef.current?.contains(event.target)
      ) {
        setActionMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [actionMenuOpen])

  const subscriptionLoading = subscribeLoading || unsubscribeLoading

  const handleSubscribe = async (userId = null) => {
    if (!baseConversationId) return
    setSubscriptionError(null)
    try {
      await subscribeConversationMutation({
        variables: {
          conversationId: baseConversationId,
          userId: userId ? userId : null,
        },
      })
    } catch (error) {
      // handled in onError
    }
  }

  const handleAddSelectedSubscriber = async () => {
    if (!selectedSubscriberId) return
    await handleSubscribe(selectedSubscriberId)
    setSelectedSubscriberId('')
    setSelectedSubscriberUser(null)
    setSubscriberSearchTerm('')
  }

  const handleSubscribeSelf = () => {
    handleSubscribe(null)
  }

  const handleUnsubscribe = async (userId) => {
    if (!baseConversationId) return
    setSubscriptionError(null)
    try {
      await unsubscribeConversationMutation({
        variables: {
          conversationId: baseConversationId,
          userId,
        },
      })
    } catch (error) {
      // handled in onError
    }
  }

  const isCurrentUserSubscribed = currentUserIdString ? subscriberIdSet.has(currentUserIdString) : false
  const canCurrentUserSubscribe = Boolean(currentUserIdString)

  if (!currentUser) return <div></div>

  if (conversationError) {
    return (
      <div className="CommsSession">
        <div className="alert alert-danger m-3">
          Failed to load conversation details.
        </div>
      </div>
    )
  }

  if (!conversationId) {
    return (
      <div className="CommsSession">
        <div className="CommsSessionPlaceholder">Select a conversation to begin.</div>
      </div>
    )
  }

  if (!conversation && !conversationLoading) {
    return (
      <div className="CommsSession">
        <div className="CommsSessionPlaceholder">Conversation not found.</div>
      </div>
    )
  }

  if (!conversation && conversationLoading) return <Loader />

  const contactName = conversation?.contact_name ?? conversation?.contactName ?? ''
  const contactPhone = conversation?.contact_phone ?? conversation?.contactPhone ?? ''
  const conversationTitle = conversationLoading ? 'Loading conversation…' : (conversation?.subject || contactName || 'Conversation')
  const driverName = conversation?.driver?.name ?? conversation?.driver?.fullName ?? ''
  const driverBranchName = conversation?.driver?.branch?.name ?? ''
  const conversationChannelName = conversation?.channel?.name ?? ''
  const isArchived = conversation?.status === "CLOSED"
  const driverId = conversation?.driver?.id ? conversation.driver.id : null
  const combinedActionError = [assignError, subscriptionError].filter(Boolean).join(' • ')

  return (
    <div>
      {!slimView && <ExternalIntegrations driverId={conversation?.driver?.id || null} />}
      <div className={`CommsSession ${slimView ? "CommsSession--slim" : ""}`}>
        <div className="CommsSessionHeader">
          <div className="CommsHeaderLeft">
            <RepAvatar keepClose={true} text={contactName || conversationTitle} />&nbsp;
            <div>
              <div className="CommsHeaderTitle">
                <Link to={`/comms/conversation/${conversation?.id}/`} className="CommsHeaderLink">
                  {conversationTitle}
                </Link>
              </div>
              <div className="CommsHeaderMeta">
                {contactPhone && <span className="CommsHeaderBadge">{contactPhone}</span>}
                {conversation?.inboxes?.edges?.length > 0 &&
                  <span className="CommsHeaderBadge CommsHeaderBadge--inbox">{conversation?.inboxes?.edges[0]?.node.name}</span>
                }
                {driverName && (
                  driverId ? (
                    <Link to={`/driver/${driverId}/`} className="CommsHeaderLink">{driverName}</Link>
                  ) : (
                    <span className="CommsHeaderBadge">{driverName}</span>
                  )
                )}
                {driverBranchName && <span className="CommsHeaderBadge">Branch: {driverBranchName}</span>}
                {conversationChannelName && <span className="CommsHeaderBadge">Channel: {conversationChannelName}</span>}
              </div>
            </div>
          </div>

          <div className="CommsHeaderRight">
            <div className="CommsHeaderUtilities">
              <div className="CommsAssignmentControl">
                <RepTypeAhead
                  reps={assigneeUsers}
                  groups={roundRobinGroups}
                  value={selectedAssigneeValue}
                  onChange={handleAssigneeChange}
                  onInputChange={(inputValue) => {
                    setAssigneeSearchTerm(inputValue)
                  }}
                  inputValue={assigneeSearchTerm}
                  onMenuClose={() => setAssigneeSearchTerm('')}
                  isLoading={assigneeOptionsLoading}
                  isDisabled={assignLoading || conversationLoading || conversationInboxIds.length === 0}
                  placeholder="Assign conversation…"
                  noOptionsMessage={() => (
                    assigneeSearchTerm.trim()
                      ? 'No matching inbox members'
                      : 'No inbox members found'
                  )}
                  loadingMessage={() => 'Loading inbox members…'}
                  disableLocalFilter
                />
              </div>
            </div>

            <div className="CommsHeaderActions">
              <button
                className={`CommsHeaderQuickActionButton${isArchived ? ' is-secondary' : ''}`}
                disabled={updateConversationLoading}
                onClick={() => {
                  setSubscribersMenuOpen(false)
                  setActionMenuOpen(false)
                  updateConversationMutation({
                    variables: { conversationId: conversation.id, status: isArchived ? "OPEN" : "CLOSED" }
                  }).then(() => {
                    !isArchived && onConversationArchived && typeof onConversationArchived === 'function' && onConversationArchived(conversation.id)
                  })
                    
                  
                }}
                aria-label={isArchived ? "Reopen conversation" : "Archive conversation"}
                type="button"
              >
                <i className={`fa fa-${isArchived ? 'undo' : 'archive'}`} aria-hidden="true"></i>
                <span>{isArchived ? 'Reopen' : 'Archive'}</span>
              </button>

              <div className="CommsSubscribersToggleWrapper" title="Subscribers">
                <button
                  ref={subscribersButtonRef}
                  type="button"
                  className="CommsHeaderMenuButton"
                  onClick={() => {
                    setSubscribersMenuOpen(prev => !prev)
                    setActionMenuOpen(false)
                  }}
                  aria-label="Manage subscribers"
                >
                  <i className="fa fa-users" aria-hidden="true"></i>
                </button>

                {subscribersMenuOpen && (
                  <div className="CommsSubscribersMenu" ref={subscribersMenuRef}>
                    <div className="CommsSubscribersMenuSection">
                      <div className="CommsSubscribersMenuLabel">Subscribers</div>
                      <div className="CommsSubscriberList">
                        {subscribers.length === 0 && (
                          <span className="CommsSubscriberPlaceholder">{conversationLoading ? 'Loading subscribers…' : 'No subscribers yet'}</span>
                        )}
                        {subscribers.map(subscriber => (
                          <div key={subscriber.id} className="CommsSubscriberChip">
                            <span>{formatUserDisplayName(subscriber)}</span>
                            <button
                              type="button"
                              className="CommsSubscriberRemove"
                              onClick={() => handleUnsubscribe(subscriber.id)}
                              disabled={subscriptionLoading}
                              title="Remove subscriber"
                            >
                              x
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="CommsSubscribersMenuSection">
                      <div className="CommsSubscribersMenuLabel">Add teammate</div>
                      <div className="CommsSubscriberTypeAhead">
                        <RepTypeAhead
                          reps={availableSubscriberReps}
                          value={selectedSubscriberValue}
                          onChange={handleSubscriberChange}
                          onInputChange={(inputValue) => {
                            setSubscriberSearchTerm(inputValue)
                          }}
                          inputValue={subscriberSearchTerm}
                          onMenuClose={() => setSubscriberSearchTerm('')}
                          isLoading={subscriberOptionsLoading}
                          isDisabled={subscriptionLoading}
                          placeholder="Search active reps…"
                          noOptionsMessage={() => (
                            subscriberSearchTerm.trim()
                              ? 'No active reps found'
                              : 'No active reps available'
                          )}
                          loadingMessage={() => 'Loading active reps…'}
                          disableLocalFilter
                          menuPortalTarget={null}
                          menuPosition="absolute"
                          minMenuWidth={240}
                          maxMenuWidth="min(360px, calc(100vw - 64px))"
                        />
                      </div>

                      <div className="CommsSubscriberButtons">
                        <button
                          type="button"
                          className="CommsSubscriberButton"
                          onClick={handleAddSelectedSubscriber}
                          disabled={subscriptionLoading || !selectedSubscriberId}
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          className="CommsSubscriberButton secondary"
                          onClick={handleSubscribeSelf}
                          disabled={subscriptionLoading || isCurrentUserSubscribed || !canCurrentUserSubscribe}
                          title={!canCurrentUserSubscribe ? 'You must be signed in to subscribe' : undefined}
                        >
                          {isCurrentUserSubscribed ? 'Subscribed' : 'Subscribe me'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                ref={actionButtonRef}
                className="CommsHeaderMenuButton"
                onClick={() => {
                  setSubscribersMenuOpen(false)
                  setActionMenuOpen(!actionMenuOpen)
                }}
                aria-label="Conversation actions"
                type="button"
              >
                <span>⋮</span>
              </button>

              {actionMenuOpen && (
                <div className="CommsHeaderMenu" ref={actionMenuRef}>
                  <ul>
                    {isCurrentUserAssigned && (
                      <li
                        onClick={() => {
                          updateConversationMutation({ variables: { conversationId: conversation.id, isReadByAssignedAgent: false } })
                          setActionMenuOpen(false)
                        }}
                        role="button"
                      >
                        Mark as Unread
                      </li>
                    )}
                    <li
                      onClick={() => {
                        setEditModalOpen(true)
                        setActionMenuOpen(false)
                      }}
                      role="button"
                    >
                      Edit Conversation
                    </li>
                    <li
                      onClick={() => {
                        setSnoozeModalOpen(true)
                        setActionMenuOpen(false)
                      }}
                      role="button"
                    >
                      Snooze
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {combinedActionError && (
          <div className="alert alert-warning" style={{ margin: '12px 0' }}>
            {combinedActionError}
          </div>
        )}

        <div className="CommsSessionBody">
          <div id="CommsSessionMessages" className="CommsSessionMessages" ref={messagesContainerRef}>
            {combinedFeed.map((item, idx) => {
              if (item.type === "note") {
                return (
                  <div
                    key={`note-${item.id}`}
                    className="CommsNote mb-2"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      textAlign: "center",
                      margin: "0 auto",
                      fontStyle: "italic",
                      color: "#666",
                      fontSize: 13,
                      background: "#f9f9f9",
                      borderRadius: 8,
                      padding: "6px 12px",
                      width: "fit-content",
                    }}
                    title={moment(item.date_added).format("LLLL")}
                  >
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => {
                          if (href?.startsWith('mention:')) {
                            return <span className="message-mention">{children}</span>
                          }
                          return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
                        }
                      }}
                    >
                      {normalizeConversationNoteMarkdown(item.note)}
                    </ReactMarkdown>
                    <span style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>
                      — {item.created_by_username}, {moment(item.date_added).fromNow()}
                    </span>
                  </div>
                )
              }

              // Key format must match baseAttachmentsMap exactly; kept in one file so a change to either can't silently drop attachments.
              const resolvedAttachments = (item.attachments ?? item?.attachment_urls ?? [])
                .map((attachment, attIndex) => (
                  attachmentsMap.get(attachment?.graphene_id ?? `${item.graphene_id}-${attIndex}`)
                ))
                .filter(Boolean)

              // The newest message is both the scroll target and the one that starts expanded.
              const isNewestMessage = idx === lastMessageIndex

              return (
                <CommsMessageCard
                  key={`msg-${item.id}`}
                  ref={isNewestMessage ? lastMessageRef : null}
                  item={item}
                  defaultOpen={isNewestMessage}
                  attachments={resolvedAttachments}
                  topics={topics}
                  isEditingTopic={editingTopicMessageId === item.graphene_id}
                  onEditTopic={setEditingTopicMessageId}
                  onTopicChange={handleTopicChange}
                />
              )
            })}
          </div>
        </div>

        <MessageComposer
          conversationId={conversationId}
          suggestedResponse={lastInboundSuggestedResponse}
          lastInboundMessage={lastInboundMessage}
        />
        {conversation && (
          <CommsSnoozeModal
            isOpen={snoozeModalOpen}
            toggle={() => setSnoozeModalOpen(false)}
            onSnooze={async (snoozedUntil) => {
              const result = await updateConversationMutation({
                variables: {
                  conversationId: conversation.id,
                  status: 'SNOOZED',
                  snoozedUntil: snoozedUntil.format(),
                },
              })
              const payload = result?.data?.messagingUpdateConversation
              if (!payload?.ok) {
                throw new Error(payload?.errors?.join(', ') || 'Failed to snooze conversation')
              }
              debouncedRefetch()
              return result
            }}
          />
        )}
        {conversation && (
          <CommsEditConversationModal
            isOpen={editModalOpen}
            toggle={() => setEditModalOpen(false)}
            conversation={conversation}
            conversationMembers={conversationMembers}
            assignableMembers={assignableMembers}
            roundRobinGroups={roundRobinGroups}
            onSave={async (input) => {
              const variables = { conversationId: conversation.id, ...input }
              const result = await updateConversationMutation({ variables })
              const ok = result?.data?.messagingUpdateConversation?.ok
              if (ok === false) {
                const errors = result?.data?.messagingUpdateConversation?.errors || []
                const message = Array.isArray(errors) && errors.length ? errors.join(', ') : 'Failed to update conversation'
                throw new Error(message)
              }
              setEditModalOpen(false)
              debouncedRefetch()
              return result
            }}
          />
        )}
      </div>
    </div>
  )
}

export default withApolloClient(CommsConversation)
